/**
 * Eraya price-test theme bridge.
 *
 * This theme (Liquify + Alpine 3 + Webflow) renders every variant price through
 * Alpine's reactive `x-text`:
 *
 *   x-text="LiquifyHelper.moneyFormat(product.selected_or_first_available_variant.price, ...)"
 *
 * The Eraya PriceTestEngine rewrites price text nodes, but Alpine re-renders
 * that same text from reactive state on every variant change / re-render, and
 * the engine's anti-clobber observer then fights it back. Instead of fighting,
 * we feed the engine's price into the state Alpine reads.
 *
 * `handleVariant()` is registered by li_custom.js on `alpine:init`. That is a
 * CLASSIC body script, so its listener is registered while the document is still
 * parsing; alpinejs3.js then ends with `queueMicrotask(() => Alpine.start())`, so
 * `alpine:init` fires and the component is built BEFORE this deferred script runs.
 * We therefore wrap the component Alpine already made (via `Alpine.$data`), not
 * the `Alpine.data` definition — so the test price arrives through the same
 * `price` getter Alpine already tracks.
 *
 * No-op for products without an active price test.
 */
(function () {
  'use strict';

  var OBSERVER_DEBOUNCE_MS = 150;

  function getManager() {
    try {
      var E = window.Eraya;
      if (E && E.sdk && typeof E.sdk.getPriceTestManager === 'function') {
        return E.sdk.getPriceTestManager();
      }
    } catch (_) {}
    return null;
  }

  /* ---------------------------------------------------------------- Alpine */

  // Test price in cents for the variant currently selected on this component,
  // or null when there is no active test (and when the SDK is not up yet).
  function testPriceFor(prod) {
    var mgr = getManager();
    var sav = prod && prod.selected_or_first_available_variant;
    if (!mgr || !sav || sav.id == null) return null;
    try {
      return mgr.getPriceByVariantId(String(sav.id));
    } catch (_) {
      return null;
    }
  }

  // The REAL price, always taken from the variant list Shopify rendered.
  // `product.variants` is never mutated by the theme, unlike
  // `selected_or_first_available_variant` (replaced on every variant change).

  // Never read `price` off an object we may have wrapped — that is the getter
  // below, and reading it recurses forever. Wrapped objects carry their real
  // price in `__erayaRealPrice`.
  function readReal(obj) {
    return obj.__erayaRealPrice != null ? obj.__erayaRealPrice : obj.price;
  }

  function realPriceFor(prod, sav) {
    var variants = prod && prod.variants;
    if (variants && variants.length) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(sav.id)) return readReal(variants[i]);
      }
    }
    return readReal(sav);
  }

  function wrapVariant(prod) {
    var sav = prod && prod.selected_or_first_available_variant;
    if (!sav || sav.__erayaPriceWrapped) return;
    var realPrice = realPriceFor(prod, sav); // read BEFORE installing the getter

    try {
      Object.defineProperty(sav, 'price', {
        configurable: true,
        get: function () {
          var test = testPriceFor(prod);
          return test != null ? test : realPrice;
        },
        // The theme writes the real price on variant change; keep it as the
        // baseline while the getter still hands out the test price.
        set: function (value) {
          realPrice = value;
        },
      });
      // Both markers must be non-enumerable. li_custom.js rebuilds this object on
      // every variant change with `{...selected_or_first_available_variant, ...product}`,
      // and an ENUMERABLE marker would be copied onto the replacement — making the
      // next wrapVariant() return early, so the replacement keeps the theme's plain
      // accessor and the swap silently shows the real price.
      Object.defineProperty(sav, '__erayaRealPrice', {
        value: realPrice,
        configurable: true,
        writable: true,
      });
      Object.defineProperty(sav, '__erayaPriceWrapped', {
        value: true,
        configurable: true,
      });
    } catch (_) {}
  }

  function wrapProduct(prod) {
    if (!prod || prod.__erayaProductWrapped) return;
    // Non-enumerable for the same reason as the variant markers above: a section
    // re-render must not be able to carry this marker onto a fresh object holding
    // an unwrapped variant.
    Object.defineProperty(prod, '__erayaProductWrapped', {
      value: true,
      configurable: true,
    });
    var current = prod.selected_or_first_available_variant;
    wrapVariant(prod);
    try {
      Object.defineProperty(prod, 'selected_or_first_available_variant', {
        configurable: true,
        get: function () {
          return current;
        },
        // The theme swaps this object on every variant change; re-wrap each time
        // so the test price survives the swap. Reassigning it is itself a reactive
        // write, so Alpine re-runs the `x-text` bindings and paints the new getter.
        set: function (v) {
          current = v;
          wrapVariant(prod);
        },
      });
    } catch (_) {}
  }

  function wrapComponent(data) {
    try {
      if (data && data.product) wrapProduct(data.product);
    } catch (_) {}
    return data;
  }

  // Wrap every handleVariant component already on the page. This — not an
  // `Alpine.data` patch — is what installs the wrap: `alpine:init` has already
  // fired by the time this deferred script runs. Called at boot, on
  // `alpine:initialized` (covers the reverse load order), and from rescan() so
  // sections/upsells rendered later are covered too.
  function patchComponents() {
    if (!window.Alpine || typeof window.Alpine.$data !== 'function') return;
    var nodes = document.querySelectorAll('[x-data*="handleVariant"]');
    for (var i = 0; i < nodes.length; i++) {
      var data = null;
      try {
        data = window.Alpine.$data(nodes[i]);
      } catch (_) {}
      if (data && data.product) wrapComponent(data);
    }
  }

  /* ------------------------------------------------------------- rescans */

  var timer = null;

  function rescan() {
    patchComponents();
    var mgr = getManager();
    if (mgr && typeof mgr.updatePricesOnCurrentPage === 'function') {
      try {
        mgr.updatePricesOnCurrentPage();
      } catch (_) {}
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(rescan, OBSERVER_DEBOUNCE_MS);
  }

  function hasProductNode(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.hasAttribute && node.hasAttribute('data-product-id')) return true;
    return typeof node.querySelector === 'function' &&
      node.querySelector('[data-product-id]') !== null;
  }

  function startObserver() {
    if (typeof MutationObserver === 'undefined' || !document.body) return;
    // childList only: attribute writes (our own + the engine's data-eraya-*)
    // must not retrigger this, or it would loop.
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (hasProductNode(added[j])) {
            schedule();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------- express checkout (BIN) */

  // `{{ form | payment_button }}` renders Shopify's accelerated-checkout button
  // (default ON in this theme's product blocks). It does NOT post to
  // /cart/add.js, so the SDK's fetch/XHR patch never sees it and the shopper
  // would pay the ORIGINAL price with no test attribution. Intercept the click
  // before the button's own handler, and route a tested variant through the
  // SDK's own cart-add + checkout.
  var BIN_SELECTOR =
    'shopify-buy-it-now-button, shopify-accelerated-checkout, ' +
    '[data-shopify="payment-button"], .shopify-payment-button, ' +
    '.product_hero_express_button';

  function gidToId(value) {
    if (value == null) return null;
    var m = String(value).match(/ProductVariant\/(\d+)/) ||
      String(value).match(/^(\d+)$/);
    return m ? m[1] : null;
  }

  // The click lands on a node inside the button's shadow DOM; composedPath()
  // pierces it, event.target does not.
  function matchedBinTarget(event) {
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.nodeType === 1 && node.matches && node.matches(BIN_SELECTOR)) {
        return node;
      }
    }
    return null;
  }

  // The product form that owns the express button. Its hidden `id` input is
  // bound to the Alpine-selected variant (blocks/product-add-to-cart.liquid:83).
  function owningForm(btn) {
    var form = btn.closest('form');
    if (form) return form;
    var byAttr = btn.getAttribute('form');
    if (byAttr) {
      var el = document.getElementById(byAttr);
      if (el) return el;
    }
    var path = btn.getRootNode && btn.getRootNode();
    if (path && path.querySelector) {
      var inner = path.querySelector('form');
      if (inner) return inner;
    }
    return null;
  }

  function variantIdFromForm(form) {
    if (!form) return null;
    var input = form.querySelector('[name="id"]');
    return input && input.value ? gidToId(input.value) : null;
  }

  // The transform keys off `_eraya_test_price`; the older per-test form is
  // suffixed `_eraya_test_price_<testId>`. Accept either.
  function hasStampedPrice(props) {
    if (!props) return false;
    for (var key in props) {
      if (key.indexOf('_eraya_test_price') === 0) return true;
    }
    return false;
  }

  function onExpressCheckoutClick(event) {
    if (event.__erayaHandled) return;
    var btn = matchedBinTarget(event);
    if (!btn) return;

    var mgr = getManager();
    if (!mgr || typeof mgr.addToCartWithTestData !== 'function') return;

    var variantId = variantIdFromForm(owningForm(btn));
    if (!variantId) return;

    var productId = null;
    try {
      productId = mgr.getProductIdFromVariantId(variantId);
    } catch (_) {}
    if (!productId) return; // not a test product — let the native button run

    var props = null;
    try {
      props = mgr.getSimpleCartProperties(String(productId), String(variantId));
    } catch (_) {}
    // Fail closed: no stamped price means no interception. The shopper pays the
    // real price via the untouched native express checkout rather than risk an
    // undercharge from an unstamped add.
    if (!hasStampedPrice(props)) return;

    event.__erayaHandled = true;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    // Pass the verified properties explicitly rather than relying on the SDK's
    // own cart-add path to recompute them — the stamp is the single source of
    // truth for what the shopper is charged.
    mgr
      .addToCartWithTestData(String(variantId), 1, props)
      .then(function () {
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        // Never leave the shopper stuck on a dead button.
        console.error('[eraya] express checkout interception failed', err);
      });
  }

  document.addEventListener('click', onExpressCheckoutClick, true);

  /* ------------------------------------------------- add-to-cart feedback net */

  // The theme's addToCartAnimation clears `is-loading` and dispatches
  // `liquiflow:cart-added` (the header snackbar) from one success branch, gated
  // on `cartupdated`'s detail containing `item.variant_id === variantId`. That
  // exact-id match is what breaks on a duplicate product: the add succeeds and
  // the item lands in the cart, but the id in the response is not the one the
  // card passed, so the spinner runs on and no notification appears.
  //
  // Don't try to out-guess the id. li_custom.js only dispatches `cartupdated`
  // WITH an items array on a successful add — its `.catch` dispatches nothing,
  // and a rejected request yields `data.items === undefined`. So the payload is
  // the reliable success signal, whatever the id turned out to be.
  var SPINNER_TIMEOUT_MS = 1000;

  function watchAddToCart() {
    var real = window.addToCartAnimation;
    if (typeof real !== 'function' || real.__erayaWrapped) return;

    function wrapped(element, variantId) {
      var done = false;
      var timer = null;

      function finish(success) {
        if (done) return;
        done = true;
        window.removeEventListener('cartupdated', onCart);
        clearTimeout(timer);
        if (!element || !element.classList) return;
        element.classList.remove('is-loading');
        if (!success) return; // a genuinely failed add: no notification to show
        // The theme's own branch already painted the success state whenever its
        // exact-variant match happened to work (we run after it), so only fill
        // the gap — never double-dispatch the snackbar.
        if (element.classList.contains('is-added')) return;
        element.classList.add('is-added');
        document.dispatchEvent(
          new CustomEvent('liquiflow:cart-added', { bubbles: true, cancelable: false })
        );
        setTimeout(function () {
          element.classList.remove('is-added');
        }, 2000);
      }

      function onCart(event) {
        if (event.detail && event.detail.length) finish(true);
      }

      // Call the theme's animation FIRST so its own `cartupdated` listener is
      // registered before ours — that keeps us second in line, which is how the
      // `is-added` de-dupe above can tell whose branch already ran.
      var result = real.apply(this, arguments);
      window.addEventListener('cartupdated', onCart);
      timer = setTimeout(function () {
        finish(false); // backstop: never leave a button spinning forever
      }, SPINNER_TIMEOUT_MS);
      return result;
    }

    wrapped.__erayaWrapped = true;
    window.addToCartAnimation = wrapped;
  }

  /* ----------------------------------------------------------------- boot */

  function ready() {
    // The SDK dispatches `eraya:ready` — sometimes before window.Eraya.sdk is
    // assigned, so poll briefly instead of trusting the event alone.
    rescan();
  }

  patchComponents();
  document.addEventListener('alpine:initialized', patchComponents);
  startObserver();
  watchAddToCart();

  if (window.ErayaReady) ready();
  window.addEventListener('eraya:ready', function () {
    setTimeout(ready, 0);
  });

  // Variant change: the theme's own post-morph event (Liquify does not use it
  // yet; harmless if it never fires). The reactive getter above is what makes
  // this work — this only nudges a rescan for anything non-reactive.
  document.addEventListener('variant:update', function (event) {
    var d = event.detail;
    if (!d) return;
    var resource = d.resource;
    if (resource && resource.id != null) {
      var root = document.querySelector('[data-variant-id="' + resource.id + '"]');
      if (root) root.setAttribute('data-variant-id', String(resource.id));
    }
    schedule();
  });

  // Poll until the SDK appears, then stop.
  var tries = 0;
  var wait = setInterval(function () {
    if (getManager()) {
      clearInterval(wait);
      ready();
    } else if (++tries > 100) {
      clearInterval(wait);
    }
  }, 100);
})();
